import React, {
    useState, useEffect, useCallback, useMemo
} from 'react';
import { Switch, Route, BrowserRouter as Router } from 'react-router-dom';
import _ from 'lodash';
// Imported First to allow Jutro styles to be overridden
import './App.scss';
import { setComponentMapOverrides } from '@jutro/uiconfig';
import {
    DropdownMenuLink,
    useModal,
    ModalNextEmitter
} from '@jutro/components';
import { useTranslator } from '@jutro/locale';
import { AppFloorPlan } from '@jutro/floorplan';
import { routeConfirmationModal } from '@jutro/router';
import vmTranslator, { messages as commonMessages } from 'gw-platform-translations';
import { ViewModelServiceFactory } from 'gw-portals-viewmodel-js';
import { WizardPageTemplate, WizardPageTemplateWithTitle } from 'gw-portals-wizard-components-ui';
import { filterCapabilityRoutes } from 'gw-portals-config-js';
import { ClaimDocumentService } from 'gw-capability-claimdocument';

import { DependencyProvider } from 'gw-portals-dependency-react';

import { ViewModelServiceContext } from 'gw-portals-viewmodel-react';
import { AccurateBreakpointPropagation } from 'gw-jutro-adapters-react';

import {
    platformComponentMap,
    platformComponents,
    ImageComponent,
} from 'gw-components-platform-react';
import { ErrorBoundary } from 'gw-portals-error-react';
import FNOLAdacWizard, {
    AdacConfirmationPage,
    AdacHeader,
    AdacFooter,
    AdacLoginPage,
    AdacCallCenterPage,
    AdacTryAgainPage,
    AdacServiceConfirmationPage,
    AdacMyDocumentsPage,
    AdacSessionExpired,
    AdacCallbackOptionsPageStandalone as AdacRecallPage,
    AdacSendNotePage,
    AdacDocumentSigningPage,
    AdacHistoryPage,
    AdacLinkExpiredPage
} from 'gw-capability-fnol-adac-react';

import { ClaimService } from 'gw-capability-claim';
import { FNOLService } from 'gw-capability-fnol';
import FaqPage from 'gw-capability-faq-react';
// eslint-disable-next-line import/no-unresolved
import appConfig from 'app-config';
import messages from './App.messages';
import appRoutes from './App.routes.metadata.json5';
import ClaimDocuments from '../pages/ClaimDocuments/ClaimDocuments';
import ConfidentialityWaiver from '../pages/ConfidentialityWaiver/ConfidentialityWaiver';
import CommunicationVariablesContext from 'gw-capability-fnol-adac-react/utils/CommunicationVariablesContext';

const { capabilitiesConfig, env } = appConfig;

const componentMap = {
    adacconfirmationpage: AdacConfirmationPage,
    faqpage: FaqPage,
    fnoladacwizard: FNOLAdacWizard,
    claimdocuments: ClaimDocuments,
    adacloginpage: AdacLoginPage,
    adaccallcenter: AdacCallCenterPage,
    tryagainlaterpage: AdacTryAgainPage,
    adacsessionexpired: AdacSessionExpired,
    confidentialitywaiver: ConfidentialityWaiver,
    adacserviceconfirmation: AdacServiceConfirmationPage,
    adacrecallpage: AdacRecallPage,
    adacsendnotepage: AdacSendNotePage,
    adacmydocumentspage: AdacMyDocumentsPage,
    adacdocumentsigningpage: AdacDocumentSigningPage,
    adachistorypage: AdacHistoryPage,
    adaclinkexpiredpage: AdacLinkExpiredPage
};

setComponentMapOverrides(
    {
        ...platformComponentMap,
        img: { component: 'img' },
        WizardPageTemplateWithTitle: { component: 'WizardPageTemplateWithTitle' },
        WizardPageTemplate: { component: 'WizardPageTemplate' },
    },
    {
        ...platformComponents,
        img: ImageComponent,
        WizardPageTemplateWithTitle,
        WizardPageTemplate
    }
);

const modalEmitter = new ModalNextEmitter();

const onUnload = (e) => {
    e.preventDefault();
    e.returnValue = '';
};

function App() {
    const {
        showAlert
    } = useModal();

    const translator = useTranslator();
    const [viewModelService, setViewModelService] = useState(undefined);
    const [communicationVariables, setCommunicationVariables] = useState({});

    useEffect(() => {
        window.addEventListener('beforeunload', onUnload);
        return () => {
            window.removeEventListener('beforeunload', onUnload);
        };
    }, []);

    const getAvatarLinks = useCallback(() => {
        return [
            <DropdownMenuLink to="/faq">{translator(messages.faq)}</DropdownMenuLink>,
        ];
    }, [translator]);

    const getLogoSrc = useCallback(() => {
        const prefix = _.get(env, 'DEPLOYMENT_PATH', '').replace(/\/$/, '');
        return `${prefix}/branding/logo-header.svg`;
    }, []);

    // (Router) Title is required and used for (Browser) title
    // in a format of "${title} - ${appName}". In order to show
    // the application name only, it needs to be an empty value.
    const routesWithoutTitle = useMemo(() => {
        return filterCapabilityRoutes(capabilitiesConfig, appRoutes.routes)
            .map((route) => {
                return {
                    ...route,
                    title: ''
                };
            });
    }, []);

    const floorPlansConfig = useMemo(() => [{
            scrollContent: false,
            showFooter: false,
            routes: routesWithoutTitle,
            showHeader: false,
            className: 'floorPlanHeight',
            header: {
                avatarChildren: getAvatarLinks(),
                className: 'appheader',
                logoSrc: getLogoSrc(),
                showAppSwitcher: false,
                showNotifications: false
            }
    }], [getAvatarLinks, getLogoSrc, routesWithoutTitle]);

    useEffect(() => {
        const translatorFn = vmTranslator(translator);
        import(
            /* webpackChunkName: "product-metadata" */
            // eslint-disable-next-line import/no-unresolved
            'product-metadata'
        ).then((productMetadata) => {
            const { default: result } = productMetadata;
            setViewModelService(
                ViewModelServiceFactory.getViewModelService(result, translatorFn)
            );
        });
    }, [translator]);

    const handleError = useCallback(() => {
        return showAlert({
            title: messages.error,
            message: messages.errorMessage,
            status: 'error',
            icon: 'mi-error-outline',
            confirmButtonText: commonMessages.ok
        });
    }, []);

    return (
        <AccurateBreakpointPropagation>
            <DependencyProvider
                value={{
                    FNOLService: FNOLService,
                    ClaimService: ClaimService,
                    ClaimDownloadService: ClaimDocumentService
                }}
            >
                <ViewModelServiceContext.Provider value={viewModelService}>
                    <CommunicationVariablesContext.Provider value = {{ communicationVariables, setCommunicationVariables }}>
                        <ErrorBoundary onError={handleError}>
                            <Router
                                basename="/ambulance-app"
                                getUserConfirmation={
                                    (message, callback) => {
                                        return routeConfirmationModal(message, callback, modalEmitter);
                                    }
                                } >
                                <Switch>
                                    <Route>
                                        <AdacHeader />
                                        <AppFloorPlan
                                            componentMap={componentMap}
                                            floorPlans={floorPlansConfig}
                                        />
                                        <AdacFooter />
                                    </Route>
                                </Switch>
                            </Router>
                        </ErrorBoundary>
                    </CommunicationVariablesContext.Provider>
                </ViewModelServiceContext.Provider>
            </DependencyProvider>
        </AccurateBreakpointPropagation>
    );
}

export default App;
